import { FC, useEffect } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Radio, Typography, useThemeValue } from '@altermaind/web/ui';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getEmployeesStatsCall } from '@/people/state';
import { Steps, getTemplatesCall, getSurveysCampaignsCall } from '../../state';
import { getSurveyCampaignByIdResponsesStatsCall } from '../../state/api';

type Props = {
  setStep: (step: Steps) => void;
  setSelectedTemplate: (template: string) => void;
  selectedTemplate: string;
};

export const StepTemplates: FC<Props> = ({ setStep, setSelectedTemplate, selectedTemplate }) => {
  const t = useTranslations('surveys.sendSurvey.stepTemplates');
  const { genericResponsiveValue } = useResponsiveStyle();

  const gapXs = useThemeValue('space', 'xs');

  const { chipsList, response: responseGetTemplates } = getTemplatesCall.hook();
  const { response: responseGetSurveysCampaigns } = getSurveysCampaignsCall.hook();
  const { execute: executeGetSurveyCampaignByIdResponsesStats } =
    getSurveyCampaignByIdResponsesStatsCall.hook();
  const { completed } = getEmployeesStatsCall.hook();

  const isTemplateDisabled = (chip: string) => {
    const templateId = responseGetTemplates?.find(tmpl => tmpl.name === chip)?.id;
    const campaign = responseGetSurveysCampaigns?.findLast(c => c.surveyTemplateId === templateId);
    if (!campaign) return false;
    const alreadyInvited = campaign.invitedUserIds?.length ?? 0;
    return completed - alreadyInvited <= 0;
  };

  useEffect(() => {
    if (!selectedTemplate) return;
    const templateId = responseGetTemplates?.find(tmpl => tmpl.name === selectedTemplate)?.id;
    const campaignId = responseGetSurveysCampaigns?.findLast(
      c => c.surveyTemplateId === templateId,
    )?.id;
    if (campaignId) {
      executeGetSurveyCampaignByIdResponsesStats({ campaignId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  return (
    <Box mb='xs' mt='m' width='100%'>
      <Typography textAlign='center' variant='p2/light'>
        {t('description')}
      </Typography>
      <Box
        display='grid'
        gridTemplateColumns={genericResponsiveValue({
          breakPoints: { desktop: '1fr 1fr' },
          defaultValue: 'repeat(2fr, 1fr)',
        })}
        style={{ gap: gapXs }}
        mt='m'
      >
        {chipsList &&
          chipsList.map(chip => {
            const disabled = isTemplateDisabled(chip);
            return (
              <Card
                onClick={() =>
                  chip && !disabled
                    ? setSelectedTemplate(selectedTemplate === chip ? '' : chip)
                    : null
                }
                key={chip}
                backgroundColor='grey/dark/background'
                style={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
                }}
              >
                <Box display='flex' flexDirection='row'>
                  <Box mt={'6px' as never} mr='xxs'>
                    <Radio
                      name={`template-${chip}`}
                      checked={selectedTemplate === chip}
                      disabled={disabled}
                    />
                  </Box>
                  <Box flex='1'>
                    <Box
                      justifyContent='space-between'
                      display='flex'
                      alignItems='center'
                      flexDirection='row'
                    >
                      <Typography variant='h3'>{chip}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            );
          })}
      </Box>
      <Box justifyContent='flex-end' display='flex' flexDirection='row'>
        <Button
          disabled={selectedTemplate === ''}
          onClick={() => setStep(Steps.STEP_CHOOSER)}
          mt='l'
          variant='primary'
        >
          {t('buttons.continue')}
        </Button>
      </Box>
    </Box>
  );
};
